import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft,
  Search,
  Users,
  Briefcase,
  Mail,
  Phone,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  UserPlus
} from 'lucide-react';

const FOUNDER_EMAIL = 'rajasalame@gmail.com';

const getAvatarGradient = (name) => {
  const gradients = [
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-violet-500 to-purple-600',
    'from-rose-500 to-pink-600',
    'from-amber-500 to-orange-600',
    'from-cyan-500 to-blue-600',
    'from-green-500 to-emerald-600',
    'from-red-500 to-rose-600',
  ];
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
};

const LaunchGuestsPage = () => {
  const { user } = useAuth();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newGuest, setNewGuest] = useState({ name: '', email: '', profession: '' });
  const [addingGuest, setAddingGuest] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', profession: '' });
  const [message, setMessage] = useState(null);

  const isFounder = user?.email === FOUNDER_EMAIL;

  const fetchGuests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/launch-campaign/status`, { withCredentials: true });
      setGuests(res.data?.guests || []);
    } catch (err) {
      console.error('Error fetching guests:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isFounder) {
      fetchGuests();
    }
  }, [isFounder, fetchGuests]);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0D47A1] animate-spin" />
      </div>
    );
  }

  if (!isFounder) {
    return <Navigate to="/dashboard" replace />;
  }

  const filtered = guests.filter(g => {
    const term = searchTerm.toLowerCase();
    return (
      (g.name || '').toLowerCase().includes(term) ||
      (g.profession || '').toLowerCase().includes(term) ||
      (g.email || '').toLowerCase().includes(term)
    );
  });

  const addGuest = async (e) => {
    e.preventDefault();
    if (!newGuest.name || !newGuest.email) return;
    setAddingGuest(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/launch-campaign/add-guest`, newGuest, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      if (res.data.success) {
        setNewGuest({ name: '', email: '', profession: '' });
        setShowAddForm(false);
        fetchGuests();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setAddingGuest(false);
    }
  };

  const deleteGuest = async (email, name) => {
    if (!window.confirm(`Supprimer ${name} (${email}) de la liste ?`)) return;
    setMessage(null);
    try {
      const res = await axios.post(`${API}/launch-campaign/delete-guest`, { email }, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      fetchGuests();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  const startEdit = (g) => {
    setEditingGuest(g.email);
    setEditForm({ name: g.name, email: g.email, profession: g.profession || '' });
  };

  const saveEdit = async (oldEmail) => {
    setMessage(null);
    try {
      const res = await axios.post(`${API}/launch-campaign/edit-guest`, { old_email: oldEmail, ...editForm }, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      setEditingGuest(null);
      fetchGuests();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Retour</span>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Title */}
        <div className="text-center mb-6 sm:mb-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00B050]/10 to-[#0D47A1]/10 text-[#0D47A1] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
            <Users className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-semibold">Gestion</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-2" data-testid="page-title">
            Utilisateurs potentiels
          </h1>
          <p className="text-slate-500 max-w-lg mx-auto text-sm sm:text-base">
            Gestion des utilisateurs potentiels de 10 Millions Investor
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Badge className="bg-[#0D47A1] text-white border-0">
              <Users className="w-3 h-3 mr-1" />
              {guests.length} utilisateur{guests.length > 1 ? 's' : ''}
            </Badge>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`max-w-2xl mx-auto rounded-lg p-3 mb-4 text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`} data-testid="page-message">
            {message.text}
          </div>
        )}

        {/* Actions: Search + Add */}
        <div className="max-w-2xl mx-auto mb-6 sm:mb-8 px-2 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher un utilisateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="guest-search"
            />
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[#00B050] hover:bg-[#009040] text-white"
            data-testid="add-user-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <form onSubmit={addGuest} className="max-w-2xl mx-auto mb-6 bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="add-user-form">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Nouvel utilisateur potentiel</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <Input
                placeholder="Nom complet *"
                value={newGuest.name}
                onChange={e => setNewGuest(prev => ({ ...prev, name: e.target.value }))}
                required
                data-testid="add-name"
              />
              <Input
                type="email"
                placeholder="Email *"
                value={newGuest.email}
                onChange={e => setNewGuest(prev => ({ ...prev, email: e.target.value }))}
                required
                data-testid="add-email"
              />
              <Input
                placeholder="Profession (optionnel)"
                value={newGuest.profession}
                onChange={e => setNewGuest(prev => ({ ...prev, profession: e.target.value }))}
                data-testid="add-profession"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={addingGuest || !newGuest.name || !newGuest.email}
                className="bg-[#00B050] hover:bg-[#009040] text-white"
                data-testid="submit-add-btn"
              >
                {addingGuest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Ajouter + Créer le compte
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} data-testid="cancel-add-btn">
                Annuler
              </Button>
            </div>
          </form>
        )}

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#0D47A1] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">Aucun utilisateur trouvé</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {filtered.map((guest, idx) => (
              <Card
                key={guest.email || idx}
                className="overflow-hidden hover:shadow-lg transition-all duration-300 border-0 bg-white"
                data-testid={`guest-card-${idx}`}
              >
                <CardContent className="p-5">
                  {editingGuest === guest.email ? (
                    /* Edit mode */
                    <div className="space-y-2" data-testid={`edit-form-${idx}`}>
                      <Input
                        value={editForm.name}
                        onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="Nom"
                        className="text-sm"
                      />
                      <Input
                        type="email"
                        value={editForm.email}
                        onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                        placeholder="Email"
                        className="text-sm"
                      />
                      <Input
                        value={editForm.profession}
                        onChange={e => setEditForm(p => ({ ...p, profession: e.target.value }))}
                        placeholder="Profession"
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => saveEdit(guest.email)} data-testid={`save-edit-${idx}`}>
                          <Check className="h-3 w-3 mr-1" /> OK
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingGuest(null)} data-testid={`cancel-edit-${idx}`}>
                          <X className="h-3 w-3 mr-1" /> Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Display mode */
                    <>
                      <div className="flex items-start gap-4 mb-3">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarGradient(guest.name)} flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0`}>
                          {(guest.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-slate-900 truncate">{guest.name}</h3>
                          {guest.profession && (
                            <p className="text-sm text-[#0D47A1] font-medium truncate flex items-center gap-1">
                              <Briefcase className="w-3 h-3 flex-shrink-0" />
                              {guest.profession}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        {guest.has_account ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">Compte actif</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 border-slate-200">Pas de compte</Badge>
                        )}
                      </div>

                      <div className="space-y-1.5 text-xs text-slate-500 mb-3">
                        {guest.email && (
                          <p className="flex items-center gap-1.5 truncate">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            {guest.email}
                          </p>
                        )}
                        {guest.phone && (
                          <p className="flex items-center gap-1.5">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            {guest.phone}
                          </p>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 pt-2 border-t border-slate-100">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                          onClick={() => startEdit(guest)}
                          data-testid={`edit-guest-${idx}`}
                        >
                          <Pencil className="h-3 w-3 mr-1" /> Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => deleteGuest(guest.email, guest.name)}
                          data-testid={`delete-guest-${idx}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Supprimer
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default LaunchGuestsPage;
